import React from "react";

export interface TooltipContextType {
  openedTooltipId: string;
  handleTooltipTrigger: (id: string) => (ev: React.MouseEvent) => void;
  removeTooltipById: (id: string) => () => void;
}

const TooltipContext = React.createContext<TooltipContextType>({
  openedTooltipId: "",
  handleTooltipTrigger: () => () => {
    //
  },
  removeTooltipById: () => () => {
    //
  },
});

export const TooltipContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // Tooltip state - only one click-triggered tooltip can be opened at a time
  const [openedTooltipId, setOpenedTooltipId] = React.useState<string>("");

  React.useLayoutEffect(() => {
    const closeAnyTooltip = () => setOpenedTooltipId("");

    const handleScroll = (ev: Event) => {
      const target = ev.target as HTMLElement;
      if (target) {
        const isInsideTooltip =
          target.closest && target.closest('[role="tooltip"]');
        if (isInsideTooltip) {
          return;
        }
      }
      closeAnyTooltip();
    };

    document.addEventListener("mousedown", closeAnyTooltip);
    document.addEventListener("dragstart", closeAnyTooltip);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", closeAnyTooltip);
      document.removeEventListener("dragstart", closeAnyTooltip);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const handleTooltipTrigger = React.useCallback(
    (id: string) => (ev: React.MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      setOpenedTooltipId(id);
    },
    []
  );

  const removeTooltipById = React.useCallback(
    (id: string) => () => {
      setOpenedTooltipId((op) => (op === id ? "" : op));
    },
    []
  );

  return (
    <TooltipContext.Provider
      value={{
        openedTooltipId,
        handleTooltipTrigger,
        removeTooltipById,
      }}>
      {children}
    </TooltipContext.Provider>
  );
};

export default TooltipContext;
